import { Card } from '../UI';
import { Input } from '../form';
import { Button } from '../UI';
import { FormikProvider, useFormik } from 'formik';
import { ContactFormPropsModel } from './ContactFormProps.model';
import { useMutation } from '@apollo/client';
import {
  COMPLETE_MEDIA_UPLOAD,
  CONTACT_BUG_POSTS,
  CONTACT_FEATURE_POSTS,
  CONTACT_WIDGETREQUEST_POSTS,
  CREATE_MEDIA_UPLOAD,
} from '../../http/apollo-queries';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import BoxAttachments from '../Attachments/BoxAttachments';
import { Buffer } from 'buffer';
import axios from 'axios';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { minChar } from '../form/Input/validators/string-validators';
import { Attachment } from '../Attachments/types';
import { logger } from '../../helpers/logger';

const ContactForm = ({
  type,
  placeholder,
  title,
  subtitle,
  buttonText = 'Send',
  buttonPosition = 'center',
  attachmentActive,
}: ContactFormPropsModel) => {
  const [createMediaUpload, { loading: createMediaUploadLoading }] =
    useMutation(CREATE_MEDIA_UPLOAD);
  const [completeMediaUpload] = useMutation(COMPLETE_MEDIA_UPLOAD);
  const [bugRequest] = useMutation(CONTACT_BUG_POSTS);
  const [featureRequest] = useMutation(CONTACT_FEATURE_POSTS);
  const [widgetRequest] = useMutation(CONTACT_WIDGETREQUEST_POSTS);
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const handleComplete = (res: any) => {
    setIsLoading(false);
    setAttachments([]);
    toast.success(t('contactForm.successSent'));

    formik.setFieldValue('text', '');
    formik.setFieldTouched('text', false);
  };

  const handleError = (err) => {
    logger.error(err);
    setIsLoading(false);
    toast.error(t('contactForm.errorSend'));
  };

  const formik = useFormik({
    initialValues: {
      text: '',
      attachments: [],
    },
    validateOnMount: true,
    onSubmit: () => {
      setIsLoading(true);

      Promise.all(
        attachments.map((item) => {
          return axios
            .get(item.preview, {
              responseType: 'arraybuffer',
            })
            .then((res) => {
              return uploadImageToS3Url(
                res.data,
                res.headers['content-type'],
                item.uploadUrl
              );
            })
            .then((s3UploadResult) => {
              return completeMediaUpload({
                variables: {
                  mediaId: item.id,
                },
              });
            })
            .then(({ data }) => {
              return Promise.resolve(data.completeMediaUpload.id);
            })
            .catch((err) => {
              logger.error('ContactForm:errorMediaUpload', err);
              toast.error(t('contactForm.errorUpload'));
              return Promise.reject();
            });
        })
      )
        .then((res: string[]) => {
          const variables: {
            text: string;
            attachments: string[];
          } = {
            text: formik.values.text,
            attachments: res,
          };

          switch (type) {
            case 'BUG':
              bugRequest({
                variables,
              })
                .then(handleComplete)
                .catch(handleError);
              break;
            case 'FEATURE':
              featureRequest({
                variables,
              })
                .then(handleComplete)
                .catch(handleError);
              break;
            case 'WIDGET':
              widgetRequest({
                variables,
              })
                .then(handleComplete)
                .catch(handleError);
              break;
          }
        })
        .catch((err) => {
          logger.error('ContactForm:error', err);
          setIsLoading(false);
          toast.error(t('contactForm.errorUpload'));
        });
    },
  });

  const uploadImageToS3Url = (
    imageFile: string,
    contentType: string,
    url: string
  ) => {
    return axios({
      method: 'put',
      url,
      data: Buffer.from(imageFile, 'base64'),
      headers: { 'Content-Type': contentType, 'Content-Encoding': 'base64' },
    });
  };

  const refId = new Date().getTime();

  // Add Attachment
  const { getInputProps, getRootProps } = useDropzone({
    accept: 'image/jpeg,image/png',
    maxFiles: 5,
    maxSize: 5000000,
    onDropAccepted: (acceptedFiles) => {
      const attachmentsCreated: Attachment[] = [];
      Promise.all(
        acceptedFiles.map((file) => {
          return createMediaUpload({
            variables: {
              filename: file.name,
              mediaType: 'CONTACT',
              refId,
            },
          }).then(({ data }) => {
            logger.log('ContactForm:createMediaUpload', data);

            attachmentsCreated.push({
              preview: URL.createObjectURL(file),
              name: file.name,
              uploadUrl: data.createMediaUpload.uploadUrl,
              key: data.createMediaUpload.key,
              id: data.createMediaUpload.id,
              type,
            });
          });
        })
      )
        .then((res) => {
          setAttachments([...attachments, ...attachmentsCreated]);
        })
        .catch((err) => {
          logger.error('ContactForm:createMediaUpload', err);
        });
    },
  });

  const handleRemoveAttachment = (event, index) => {
    let tmp = [...attachments];
    tmp.splice(index, 1);
    setAttachments(tmp);
  };

  return (
    <div className={`w-full mx-auto`}>
      <Card className={`bg-white h-full relative`} title={title}>
        <div className={`text-center pt-5`}>
          <span className={'text-grey-50'}>{subtitle}</span>
        </div>

        <div className={`text-center w-full m-auto my-6`}>
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} className={`px-4`}>
              <Input
                name="text"
                formik={formik}
                id="text"
                label={placeholder}
                placeholder={placeholder}
                type={'textarea'}
                size={`full`}
                errorMessage={formik.errors.text}
                validate={(val: string) => {
                  if (!val) {
                    return true;
                  }

                  const isError = minChar(val, 50);
                  if (isError) {
                    return t('contactForm.charLimitNotMet');
                  }

                  return null;
                }}
                className={'columns-12 m-auto mt-6 mb-2 h-36'}
              />
              <div
                className={`flex flex-row items-center ${
                  buttonPosition === 'right'
                    ? 'justify-between'
                    : 'justify-center'
                }`}
              >
                <div className={'text-left'}>
                  {attachmentActive && (
                    <BoxAttachments
                      removeAttachment={handleRemoveAttachment}
                      attachments={attachments}
                      inputProps={getInputProps}
                      rootProps={getRootProps}
                      loading={createMediaUploadLoading}
                    />
                  )}
                </div>

                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    buttonClass="w-64 mt-8 m-auto"
                    loading={isLoading}
                    disabled={!!formik.errors.text}
                  >
                    {buttonText}
                  </Button>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Card>
    </div>
  );
};
export default ContactForm;
